import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Factory, Search, ChevronUp, ChevronDown, TrendingUp,
  AlertCircle, Package, DollarSign, BarChart2, Truck,
} from 'lucide-react';
import { MainLayout } from '../../components/layout/MainLayout';
import { forecastService, ForecastPart, ForecastChannel } from '../../api/services/forecastService';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function firstOfYearStr() {
  return `${new Date().getFullYear()}-01-01`;
}

function fmtRevenue(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function fmtNum(n: number) {
  return n.toLocaleString('en-US');
}

type Horizon = 30 | 60 | 90;
type SortKey = 'units' | 'velocity' | 'forecast' | 'revenue' | 'orders';
type SortDir = 'asc' | 'desc';

function priorityOf(velocity: number, horizon: Horizon): 'HIGH' | 'MEDIUM' | 'LOW' {
  const projected = velocity * horizon;
  if (projected >= 20) return 'HIGH';
  if (projected >= 8)  return 'MEDIUM';
  return 'LOW';
}

const PRIORITY_STYLES = {
  HIGH:   'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  MEDIUM: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  LOW:    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};

// ─── Stat card ───────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  color: string;
  icon: React.ReactNode;
}> = ({ label, value, sub, color, icon }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-5 py-4 flex items-start gap-4">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
      {icon}
    </div>
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">{label}</p>
      <p className="text-2xl font-extrabold text-gray-900 dark:text-white tabular-nums mt-0.5">{value}</p>
      {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Channel card ─────────────────────────────────────────────────────────────

const ChannelCard: React.FC<{ ch: ForecastChannel; rank: number; maxUnits: number; totalUnits: number }> = ({
  ch, rank, maxUnits, totalUnits,
}) => {
  const pct      = maxUnits > 0 ? (ch.UnitsSold / maxUnits) * 100 : 0;
  const sharePct = totalUnits > 0 ? ((ch.UnitsSold / totalUnits) * 100).toFixed(1) : '0.0';
  const rankColors = ['bg-amber-500', 'bg-slate-400', 'bg-orange-600'];
  const badgeBg    = rankColors[rank - 1] ?? 'bg-gray-200 dark:bg-gray-700';

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0 ${badgeBg}`}>
          {rank}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-gray-900 dark:text-white truncate">{ch.Channel}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">{fmtNum(ch.UniquePartNumbers)} SKUs · {fmtNum(ch.OrderCount)} orders</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400 tabular-nums leading-none">{fmtNum(ch.UnitsSold)}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sharePct}%</p>
        </div>
      </div>
      <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%`, transition: 'width 0.5s ease' }} />
      </div>
      <div className="grid grid-cols-3 gap-2 pt-1">
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
          <p className="text-xs text-gray-400 dark:text-gray-500">Revenue</p>
          <p className="text-sm font-bold text-green-600 dark:text-green-400 tabular-nums">{fmtRevenue(ch.Revenue)}</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
          <p className="text-xs text-gray-400 dark:text-gray-500">Avg / unit</p>
          <p className="text-sm font-bold text-gray-800 dark:text-gray-200 tabular-nums">{fmtRevenue(ch.AvgRevenuePerUnit)}</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
          <p className="text-xs text-gray-400 dark:text-gray-500">Drop Ship</p>
          <p className="text-sm font-bold text-blue-600 dark:text-blue-400 tabular-nums">{ch.DropShipPct.toFixed(0)}%</p>
        </div>
      </div>
    </div>
  );
};

// ─── Sort header ─────────────────────────────────────────────────────────────

const SortTh: React.FC<{
  label: string;
  col: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  className?: string;
}> = ({ label, col, current, dir, onSort, className = '' }) => (
  <th
    className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-900 dark:hover:text-white transition-colors ${className}`}
    onClick={() => onSort(col)}
  >
    <div className="flex items-center gap-1">
      {label}
      {current === col
        ? dir === 'desc'
          ? <ChevronDown size={12} className="text-indigo-500" />
          : <ChevronUp size={12} className="text-indigo-500" />
        : <ChevronDown size={12} className="opacity-30" />}
    </div>
  </th>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const ManufacturingForecast: React.FC = () => {
  const [dateFrom,  setDateFrom]  = useState(firstOfYearStr());
  const [dateTo,    setDateTo]    = useState(todayStr());
  const [horizon,   setHorizon]   = useState<Horizon>(30);
  const [search,    setSearch]    = useState('');
  const [sortKey,   setSortKey]   = useState<SortKey>('units');
  const [sortDir,   setSortDir]   = useState<SortDir>('desc');
  const [priority,  setPriority]  = useState<'ALL' | 'HIGH' | 'MEDIUM' | 'LOW'>('ALL');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['manufacturing-forecast', dateFrom, dateTo],
    queryFn:  () => forecastService.getManufacturingForecast(dateFrom, dateTo),
    enabled:  !!dateFrom && !!dateTo,
  });

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const processed = useMemo((): (ForecastPart & { priority: 'HIGH' | 'MEDIUM' | 'LOW'; ForecastedUnits: number })[] => {
    if (!data) return [];
    return data.parts.map(p => ({
      ...p,
      ForecastedUnits: p.DailyVelocity * horizon,
      priority: priorityOf(p.DailyVelocity, horizon),
    }));
  }, [data, horizon]);

  const displayed = useMemo(() => {
    let rows = [...processed];

    if (priority !== 'ALL') rows = rows.filter(r => r.priority === priority);

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r =>
        r.PartNumber.toLowerCase().includes(q) ||
        r.TopChannel.toLowerCase().includes(q),
      );
    }

    rows.sort((a, b) => {
      let av = 0, bv = 0;
      if (sortKey === 'units')    { av = a.UnitsSold;       bv = b.UnitsSold; }
      if (sortKey === 'velocity') { av = a.DailyVelocity;   bv = b.DailyVelocity; }
      if (sortKey === 'forecast') { av = a.ForecastedUnits; bv = b.ForecastedUnits; }
      if (sortKey === 'revenue')  { av = a.Revenue;         bv = b.Revenue; }
      if (sortKey === 'orders')   { av = a.OrderCount;      bv = b.OrderCount; }
      return sortDir === 'desc' ? bv - av : av - bv;
    });

    return rows;
  }, [processed, search, priority, sortKey, sortDir]);

  const maxChannelUnits  = data?.channels[0]?.UnitsSold ?? 1;
  const totalChannelUnits = data?.channels.reduce((s, c) => s + c.UnitsSold, 0) ?? 1;

  const countHigh   = useMemo(() => processed.filter(r => r.priority === 'HIGH').length,   [processed]);
  const countMedium = useMemo(() => processed.filter(r => r.priority === 'MEDIUM').length, [processed]);
  const countLow    = useMemo(() => processed.filter(r => r.priority === 'LOW').length,    [processed]);

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
              <Factory size={20} className="text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manufacturing Forecast</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Demand forecast and channel analysis based on historical orders
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-gray-500 dark:text-gray-400 font-medium">From</label>
            <input
              type="date"
              value={dateFrom}
              max={dateTo}
              onChange={e => setDateFrom(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <label className="text-xs text-gray-500 dark:text-gray-400 font-medium">To</label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              max={todayStr()}
              onChange={e => setDateTo(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700
                          flex flex-col items-center justify-center py-24 gap-4">
            <AlertCircle size={32} className="text-red-400" />
            <p className="text-red-500 text-sm font-medium">Failed to load forecast data.</p>
            <button onClick={() => refetch()} className="text-sm text-indigo-600 dark:text-indigo-400 underline">
              Retry
            </button>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !isError && data && data.parts.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700
                          flex flex-col items-center justify-center py-24 gap-2">
            <Package size={32} className="text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-400 dark:text-gray-500 font-medium">No orders found for the selected date range.</p>
          </div>
        )}

        {/* Main content */}
        {!isLoading && !isError && data && data.parts.length > 0 && (
          <>
            {/* Summary stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Total Units Sold"
                value={fmtNum(data.totalUnits)}
                sub={`${data.rangeDays} day window`}
                color="bg-indigo-100 dark:bg-indigo-900/40"
                icon={<Package size={18} className="text-indigo-600 dark:text-indigo-400" />}
              />
              <StatCard
                label="Total Revenue"
                value={fmtRevenue(data.totalRevenue)}
                color="bg-green-100 dark:bg-green-900/40"
                icon={<DollarSign size={18} className="text-green-600 dark:text-green-400" />}
              />
              <StatCard
                label="Unique SKUs"
                value={fmtNum(data.parts.length)}
                color="bg-amber-100 dark:bg-amber-900/40"
                icon={<BarChart2 size={18} className="text-amber-600 dark:text-amber-400" />}
              />
              <StatCard
                label="Sales Channels"
                value={fmtNum(data.channels.length)}
                color="bg-blue-100 dark:bg-blue-900/40"
                icon={<Truck size={18} className="text-blue-600 dark:text-blue-400" />}
              />
            </div>

            {/* Sales Channels */}
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <span className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest whitespace-nowrap">
                  Best Sales Channels
                </span>
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                <span className="text-xs text-gray-400 dark:text-gray-500">ranked by units sold</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.channels.map((ch, i) => (
                  <ChannelCard
                    key={ch.Channel}
                    ch={ch}
                    rank={i + 1}
                    maxUnits={maxChannelUnits}
                    totalUnits={totalChannelUnits}
                  />
                ))}
              </div>
            </div>

            {/* Forecast horizon toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <span className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest whitespace-nowrap">
                  Manufacturing Priority List
                </span>
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700 hidden sm:block" />
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {/* Horizon */}
                <div className="flex items-center gap-1.5">
                  <TrendingUp size={14} className="text-gray-400" />
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Forecast horizon:</span>
                  {([30, 60, 90] as Horizon[]).map(h => (
                    <button
                      key={h}
                      onClick={() => setHorizon(h)}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                        horizon === h
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {h}d
                    </button>
                  ))}
                </div>

                {/* Priority filter */}
                <div className="flex items-center gap-1">
                  {(['ALL', 'HIGH', 'MEDIUM', 'LOW'] as const).map(p => (
                    <button
                      key={p}
                      onClick={() => setPriority(p)}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                        priority === p
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {p === 'ALL' ? `All (${processed.length})` : p === 'HIGH' ? `High (${countHigh})` : p === 'MEDIUM' ? `Med (${countMedium})` : `Low (${countLow})`}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search part number or channel…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-8">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Part Number</th>
                      <SortTh label="Units Sold"  col="units"    current={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortTh label="Daily Vel."  col="velocity" current={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortTh label={`Fcst ${horizon}d`} col="forecast" current={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortTh label="Revenue"     col="revenue"  current={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortTh label="Orders"      col="orders"   current={sortKey} dir={sortDir} onSort={handleSort} />
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Best Channel</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                    {displayed.map((row, idx) => (
                      <tr key={row.PartNumber} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500 tabular-nums">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm font-semibold text-gray-900 dark:text-white">{row.PartNumber}</span>
                        </td>
                        <td className="px-4 py-3 tabular-nums font-semibold text-gray-800 dark:text-gray-200">{fmtNum(row.UnitsSold)}</td>
                        <td className="px-4 py-3 tabular-nums text-gray-600 dark:text-gray-400">
                          {row.DailyVelocity.toFixed(2)}<span className="text-xs ml-0.5 text-gray-400">/d</span>
                        </td>
                        <td className="px-4 py-3 tabular-nums font-bold text-indigo-600 dark:text-indigo-400">
                          {Math.round(row.ForecastedUnits).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-green-600 dark:text-green-400 font-semibold">{fmtRevenue(row.Revenue)}</td>
                        <td className="px-4 py-3 tabular-nums text-gray-600 dark:text-gray-400">{fmtNum(row.OrderCount)}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{row.TopChannel}</span>
                          <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">({fmtNum(row.TopChannelUnits)} u)</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${PRIORITY_STYLES[row.priority]}`}>
                            {row.priority}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {displayed.length === 0 && (
                <div className="text-center py-12 text-sm text-gray-400 dark:text-gray-500">
                  No results match your filters.
                </div>
              )}

              {displayed.length > 0 && (
                <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500">
                  Showing {displayed.length.toLocaleString()} of {data.parts.length.toLocaleString()} SKUs
                  &nbsp;·&nbsp;
                  Forecast horizon: <span className="font-semibold text-indigo-500">{horizon} days</span>
                  &nbsp;·&nbsp;
                  Historical window: <span className="font-semibold">{data.rangeDays} days</span>
                </div>
              )}
            </div>
          </>
        )}

      </div>
    </MainLayout>
  );
};

export default ManufacturingForecast;
